import React, {useRef, useEffect, useCallback} from 'react';
import Portal from '../Portal/Portal';
import styles from './Tooltip.scss';
import blockStyles from '@baseStyles/block.scss';

// Dev helper: append ?keepTooltip=1 to the URL to disable all close paths (click-outside, hover-leave).
// Lets you inspect the popup in DevTools without it disappearing.
const KEEP_OPEN = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('keepTooltip');

const Tooltip = ({
					 id = '',
					 title = '',
					 text = '',
					 tooltipText = '',
					 openedId = '',
					 setOpenedId = null,
				 }) => {

	const buttonRef = useRef(null);
	const tooltipRef = useRef(null);
	const placementRef = useRef(null); // 'below' | 'above' — locked at first positioning to avoid scroll-flip flicker
	const showTimerRef = useRef(null);
	const hideTimerRef = useRef(null);
	const isMountedRef = useRef(true);
	const fontRafRef = useRef(null);

	const isOpen = openedId === id;

	const toggle = () => {
		setOpenedId(isOpen ? null : id);
	};

	const close = () => {
		if (KEEP_OPEN) {
			return;
		}
		setOpenedId(null);
	};

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	// Hover-intent helpers (text-link variant only). Delay before show prevents accidental triggering;
	// short delay before hide allows the cursor to cross the gap between trigger and popup.
	const clearShowTimer = () => {
		if (showTimerRef.current) {
			clearTimeout(showTimerRef.current);
			showTimerRef.current = null;
		}
	};
	const clearHideTimer = () => {
		if (hideTimerRef.current) {
			clearTimeout(hideTimerRef.current);
			hideTimerRef.current = null;
		}
	};
	const scheduleOpen = () => {
		clearHideTimer();

		if (isOpen || showTimerRef.current) {
			return;
		}

		showTimerRef.current = setTimeout(() => {
			showTimerRef.current = null;
			setOpenedId(id);
		}, 500);
	};
	const scheduleClose = () => {
		clearShowTimer();

		if (KEEP_OPEN) {
			return;
		}

		if (!isOpen || hideTimerRef.current) {
			return;
		}

		hideTimerRef.current = setTimeout(() => {
			hideTimerRef.current = null;
			setOpenedId(null);
		}, 150);
	};

	// Cleanup any pending timers on unmount
	useEffect(() => () => {
		clearShowTimer();
		clearHideTimer();
	}, []);

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	const reposition = useCallback(() => {
		const button = buttonRef.current;
		const tooltip = tooltipRef.current;
		if (!button || !tooltip) {
			return;
		}

		const content = tooltip.firstElementChild; // .tooltipContent wrapper
		const paragraph = tooltip.querySelector('p');

		// Reset inline overrides before remeasuring
		if (content) {
			content.style.maxHeight = '';
			content.style.overflowY = '';
		}
		tooltip.style.removeProperty('--tooltip-max-width');
		if (paragraph) {
			paragraph.style.display = '';
			paragraph.style.webkitLineClamp = '';
			paragraph.style.webkitBoxOrient = '';
			paragraph.style.overflow = '';
		}

		// 1) Content-based width: default 50ch (≤5 lines), widen to 80ch (6-10 lines), clamp at 10 lines (>10)
		if (paragraph) {
			const computed = window.getComputedStyle(paragraph);
			let lineHeight = parseFloat(computed.lineHeight);
			if (Number.isNaN(lineHeight) || lineHeight <= 0) {
				lineHeight = parseFloat(computed.fontSize) * 1.4;
			}
			const measureLines = () => Math.round(paragraph.scrollHeight / lineHeight);
			let lines = measureLines();

			if (lines > 5) {
				tooltip.style.setProperty('--tooltip-max-width', '80ch');
				lines = measureLines();

				if (lines > 10) {
					paragraph.style.display = '-webkit-box';
					paragraph.style.webkitLineClamp = '10';
					paragraph.style.webkitBoxOrient = 'vertical';
					paragraph.style.overflow = 'hidden';
				}
			}
		}

		// 2) Viewport-bound positioning (position: fixed)
		const btnRect = button.getBoundingClientRect();
		const ttRect = tooltip.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// Resolve fluid CSS variables (margin, gap, x-offset) to px via probes
		// (getPropertyValue would return the raw declared value like "1.6rem" or "clamp(...)", not px)
		const probe = document.createElement('span');
		probe.style.cssText = 'position:absolute;visibility:hidden;width:var(--tooltip-viewport-margin);height:var(--tooltip-gap);';
		tooltip.appendChild(probe);
		const probeRect = probe.getBoundingClientRect();
		const viewportMargin = probeRect.width || 16;
		const gap = probeRect.height || 8;
		probe.remove();

		const xProbe = document.createElement('span');
		xProbe.style.cssText = 'position:absolute;visibility:hidden;height:0;width:var(--tooltip-x-offset);';
		tooltip.appendChild(xProbe);
		const xOffset = xProbe.getBoundingClientRect().width || 0;
		xProbe.remove();

		// Horizontal: offset from button's left edge so popup doesn't cover trigger column, clamp inside viewport.
		// Offset is only applied for the text-link variant; the icon "?" button doesn't need it.
		let left = btnRect.left + (text ? xOffset : 0);
		if (left + ttRect.width > vw - viewportMargin) {
			left = vw - viewportMargin - ttRect.width;
		}
		if (left < viewportMargin) left = viewportMargin;

		// Vertical: lock placement on first positioning, only update coords on subsequent repositions
		const spaceBelow = vh - btnRect.bottom - gap - viewportMargin;
		const spaceAbove = btnRect.top - gap - viewportMargin;

		if (placementRef.current === null) {
			placementRef.current = (ttRect.height > spaceBelow && spaceAbove > spaceBelow) ? 'above' : 'below';
		}

		let top;
		if (placementRef.current === 'above') {
			if (ttRect.height > spaceAbove) {
				top = viewportMargin;
				if (content) {
					content.style.maxHeight = `${Math.max(spaceAbove, 0)}px`;
					content.style.overflowY = 'auto';
				}
			} else {
				top = btnRect.top - gap - ttRect.height;
			}
		} else {
			top = btnRect.bottom + gap;
			if (ttRect.height > spaceBelow) {
				if (content) {
					content.style.maxHeight = `${Math.max(spaceBelow, 0)}px`;
					content.style.overflowY = 'auto';
				}
			}
		}

		// Round to integer pixels to keep text & underline AA stable across scroll repositions
		tooltip.style.left = `${Math.round(left)}px`;
		tooltip.style.top = `${Math.round(top)}px`;

		// 3) Arrow: point to button's center, side opposite to placement
		const finalRect = tooltip.getBoundingClientRect();
		const buttonCenterX = btnRect.left + btnRect.width / 2;
		const arrowPadding = 12; // breathing room from tooltip corners
		let arrowX = buttonCenterX - left;
		arrowX = Math.max(arrowPadding, Math.min(finalRect.width - arrowPadding, arrowX));
		tooltip.style.setProperty('--arrow-x', `${arrowX}px`);
		tooltip.dataset.arrowSide = placementRef.current === 'above' ? 'bottom' : 'top';

		tooltip.style.visibility = 'visible';
	}, [text]);

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	// Reposition on scroll/resize while open
	useEffect(() => {
		if (!isOpen) {
			placementRef.current = null; // reset locked placement when closed
			return;
		}

		const raf = requestAnimationFrame(reposition);
		const onScrollOrResize = () => reposition();
		// capture: true catches scroll events from nested scrollers (e.g. .tableWrap)
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);

		return () => {
			cancelAnimationFrame(raf);
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [isOpen, text, tooltipText, reposition]);

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	// Callback ref: handles Portal's async mount — fires reposition as soon as popup is in DOM.
	// Waits for web fonts to load first; otherwise initial layout uses fallback font metrics,
	// and the underline ends up at a fractional Y position which renders with AA (looks gray).
	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			if (fontRafRef.current) {
				cancelAnimationFrame(fontRafRef.current);
				fontRafRef.current = null;
			}
		};
	}, []);

	const setTooltipRef = useCallback((node) => {
		tooltipRef.current = node;
		if (node && isOpen) {
			const run = () => {
				if (!isMountedRef.current) {
					return;
				}
				fontRafRef.current = requestAnimationFrame(reposition);
			};
			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(run);
			} else {
				run();
			}
		}
	}, [isOpen, reposition]);

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	// Click outside, close popup
	useEffect(() => {
		if (!isOpen) return;

		const onPointerDown = (e) => {
			const button = buttonRef.current;
			const tooltip = tooltipRef.current;
			if (button && button.contains(e.target)) return;
			if (tooltip && tooltip.contains(e.target)) return;

			close();
		};

		document.addEventListener('pointerdown', onPointerDown, true);

		return () => {
			document.removeEventListener('pointerdown', onPointerDown, true);
		};
	}, [isOpen, setOpenedId]);

	////////////////////////////////////////////////////////////////////////////////////////////////////////
	////////////////////////////////////////////////////////////////////////////////////////////////////////

	return (
	  <div className={styles.container} role={'tooltip'}>
		  {/* BUTTONS FOR OPEN TOOLTIP (TEXT OR ICON) */}
		  {text ?
			<button
			  ref={buttonRef}
			  className={blockStyles.infoLinkReverse}
			  type={'button'}
			  title={title !== '' ? title : null}
			  onPointerEnter={(e) => {
				  if (e.pointerType === 'mouse') scheduleOpen();
			  }}
			  onPointerLeave={(e) => {
				  if (e.pointerType === 'mouse') scheduleClose();
			  }}
			  onClick={toggle}
			  dangerouslySetInnerHTML={{__html: text}}
			/>
			:
			<button
			  ref={buttonRef}
			  className={`${styles.openIconButton}`}
			  type={'button'}
			  title={title !== '' ? title : null}
			  onClick={toggle}
			>
				<span>?</span>
			</button>
		  }

		  {/* TOOLTIP popup — rendered via portal, positioned with fixed coords */}
		  {isOpen && (
			<Portal containerId={'modalContainer'}>
				<div
				  ref={setTooltipRef}
				  className={styles.tooltip}
				  onPointerEnter={text ? clearHideTimer : undefined}
				  onPointerLeave={text ? ((e) => {
					  if (e.pointerType === 'mouse') scheduleClose();
				  }) : undefined}
				>
					<div className={styles.tooltipContent}>
						{text ?
						  <p dangerouslySetInnerHTML={{__html: tooltipText}}/>
						  :
						  <p>{tooltipText}</p>
						}
					</div>
				</div>
			</Portal>
		  )}
	  </div>
	);
};

export default Tooltip;